import { AlertDialog, Button } from "@pej/kore/components";
import type { AssetSummary } from "./asset-types";

export interface AssetDeleteConfirmProps {
    asset: AssetSummary | null;
    /** Whether the asset is referenced by the template currently being edited —
     *  the one usage this confirmation can actually verify. */
    usedByThisTemplate: boolean;
    /** The workspace's asset quota ceiling, spoken in the confirmation's own
     *  words rather than a number that drifts the moment a workspace's limit
     *  differs from whatever was hardcoded here (I4, final review). */
    quotaLimit: number;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * The confirmation shown before an asset is deleted. It admits exactly what
 * it knows: nothing tracks which templates reference an asset, so usage in
 * general cannot be checked — but the template open right now is the one
 * case that can be, so this names that usage when it applies instead of
 * staying silent about a risk it happens to have the answer to.
 *
 * Deliberately no type-to-echo field. The risk here is not knowing what else
 * references the asset; typing the filename back doesn't address that, it
 * only trains operators to power through confirmations. Built on kore's
 * `AlertDialog` rather than `DestructiveConfirm` — `DestructiveConfirm`'s
 * whole purpose elsewhere in this app (see `IrreversibleConfirmDialog`) is
 * that typed-echo friction, which is exactly what this dialog must not add.
 * `AlertDialog`'s compound API gives free-form body content and a
 * hand-composed footer, which is what a pending-state-aware confirm with no
 * echo box needs.
 */
export function AssetDeleteConfirm({
    asset,
    usedByThisTemplate,
    quotaLimit,
    isPending,
    onConfirm,
    onCancel,
}: AssetDeleteConfirmProps) {
    // No asset pending deletion: render nothing at all, not a closed dialog —
    // the caller flips `asset` between null and a value rather than
    // mounting/unmounting this component itself.
    if (!asset) return null;

    return (
        <AlertDialog
            open
            onOpenChange={(open) => {
                // Backdrop click / Escape while a delete is in flight must not
                // fire onCancel — the mutation is already committed to the
                // server, so "cancelling" it now would just desync the caller's
                // state from what actually happened.
                if (!open && !isPending) onCancel();
            }}
        >
            <AlertDialog.Content>
                <AlertDialog.Header>
                    <AlertDialog.Title>Delete "{asset.displayName}"?</AlertDialog.Title>
                    <AlertDialog.Description>
                        <span className="block">
                            Other uses of this asset cannot be checked — nothing tracks which templates reference it.
                            {usedByThisTemplate && " It is used by the template you are editing."}
                        </span>
                        <span className="mt-2 block">
                            Deleting it frees one of the workspace's {quotaLimit} asset slots. This cannot be undone.
                        </span>
                    </AlertDialog.Description>
                </AlertDialog.Header>
                <AlertDialog.Footer>
                    <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button type="button" variant="destructive-solid" onClick={onConfirm} disabled={isPending}>
                        {isPending ? "Deleting…" : "Delete permanently"}
                    </Button>
                </AlertDialog.Footer>
            </AlertDialog.Content>
        </AlertDialog>
    );
}
